import { useCallback, useState } from 'react';
import type { UpgradeReason } from '../components/UpgradeDialog.js';

const VALID: ReadonlySet<string> = new Set<UpgradeReason>([
  'trial_expired',
  'quota_exhausted',
  'no_subscription',
]);

/**
 * Map a server 402 `code` onto the reason we show.
 *
 * Falls back to `trial_expired` for a missing or unrecognised code: a 402
 * always means "blocked on payment", so dropping the prompt entirely would
 * leave the user staring at a dead button with no explanation. Exported for
 * direct testing.
 */
export function upgradeReasonFromCode(code: unknown): UpgradeReason {
  return typeof code === 'string' && VALID.has(code) ? (code as UpgradeReason) : 'trial_expired';
}

/**
 * Shared handling for the server's 402 responses.
 *
 * Both gated call sites (document upload, AI extraction) need the same
 * behaviour: recognise a 402, pull out the machine-readable `code`, and show
 * the matching upgrade copy. Keeping it in one hook stops the two from
 * drifting apart.
 */
export function useUpgradePrompt() {
  const [reason, setReason] = useState<UpgradeReason | null>(null);

  const clear = useCallback(() => setReason(null), []);

  /**
   * Inspect a failed response. Returns true if it was a payment gate (and the
   * prompt is now showing), so callers can skip their generic error path.
   *
   * NOTE: this consumes the response body, so only call it on a non-ok
   * response you are not going to read yourself.
   */
  const handleResponse = useCallback(async (res: Response): Promise<boolean> => {
    if (res.status !== 402) return false;
    const body = (await res.json().catch(() => null)) as { code?: string } | null;
    setReason(upgradeReasonFromCode(body?.code));
    return true;
  }, []);

  return { reason, clear, handleResponse, show: setReason };
}
